/**
 * 注册表单组件
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { RegisterCredentials } from '../../types/auth';

interface RegisterFormProps {
  onSwitchToLogin: () => void;
  onRegistrationSuccess?: (email: string) => void;
  initialInvitationCode?: string; // URL参数传入的邀请码
}

interface InvitationValidation {
  isValid: boolean;
  error?: string;
  invitation?: {
    code: string;
    usageLimit: number;
    usedCount: number;
    description?: string;
  };
}

export function RegisterForm({ onSwitchToLogin, onRegistrationSuccess, initialInvitationCode }: RegisterFormProps): JSX.Element {
  const { register, clearError } = useAuth();
  const [formData, setFormData] = useState<RegisterCredentials & { confirmPassword: string; invitationCode: string }>({
    email: '',
    password: '',
    confirmPassword: '',
    invitationCode: initialInvitationCode || '',
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // 清除错误信息
    if (submitError) {
      setSubmitError('');
    }
    
    // 清除对应字段的验证错误
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // 邮箱验证
    if (!formData.email) {
      errors.email = '邮箱地址不能为空';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = '请输入有效的邮箱地址';
    }

    // 密码验证
    if (!formData.password) {
      errors.password = '密码不能为空';
    } else if (formData.password.length < 6) {
      errors.password = '密码至少需要6个字符';
    }

    // 确认密码验证
    if (!formData.confirmPassword) {
      errors.confirmPassword = '请确认您的密码';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = '两次输入的密码不一致';
    }

    // 邀请码验证
    if (!formData.invitationCode.trim()) {
      errors.invitationCode = '邀请码不能为空';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 清除之前的错误
    setSubmitError('');
    clearError();
    
    if (!validateForm()) {
      return;
    }

    const { confirmPassword, ...registerData } = formData;
    
    setIsSubmitting(true);
    try {
      const result = await register(registerData);
      
      // 根据注册结果决定下一步操作
      if (result.requiresVerification) {
        // 需要邮箱验证：显示验证引导页面
        if (onRegistrationSuccess) {
          onRegistrationSuccess(formData.email);
        }
      } else {
        // 已验证用户：自动登录成功，无需额外操作
        // AuthContext会自动处理登录状态
      }
    } catch (err) {
      // 本地处理注册错误，不传播到AuthContext
      const errorMessage = err instanceof Error ? err.message : '注册失败，请重试';
      setSubmitError(errorMessage);
      console.error('注册失败:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 基础表单验证（用于按钮状态）
  const isFormValid = formData.email.trim() && 
                      formData.password.trim() && 
                      formData.confirmPassword.trim() && 
                      formData.invitationCode.trim();

  return (
    <div className="w-full max-w-md">
      <h2 className="text-2xl font-bold text-center text-gray-900 mb-6">
        注册 PromptMaster
      </h2>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* 邀请码输入 */}
        <div>
          <label htmlFor="invitationCode" className="block text-sm font-medium text-gray-700 mb-1">
            邀请码 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="invitationCode"
            name="invitationCode"
            value={formData.invitationCode}
            onChange={handleInputChange}
            required
            disabled={isSubmitting}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${
              validationErrors.invitationCode ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="请输入邀请码"
          />
          {validationErrors.invitationCode && (
            <p className="mt-1 text-sm text-red-600">{validationErrors.invitationCode}</p>
          )}
        </div>

        {/* 邮箱输入 */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            邮箱地址 <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            disabled={isSubmitting}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${
              validationErrors.email ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="请输入您的邮箱"
          />
          {validationErrors.email && (
            <p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>
          )}
        </div>

        {/* 密码输入 */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            密码 <span className="text-red-500">*</span>
          </label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            required
            disabled={isSubmitting}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${
              validationErrors.password ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="请输入您的密码（至少6个字符）"
          />
          {validationErrors.password && (
            <p className="mt-1 text-sm text-red-600">{validationErrors.password}</p>
          )}
        </div>

        {/* 确认密码输入 */}
        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
            确认密码 <span className="text-red-500">*</span>
          </label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            required
            disabled={isSubmitting}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${
              validationErrors.confirmPassword ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="请再次输入您的密码"
          />
          {validationErrors.confirmPassword && (
            <p className="mt-1 text-sm text-red-600">{validationErrors.confirmPassword}</p>
          )}
        </div>

        {/* 服务端错误信息 */}
        {submitError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
            {submitError}
          </div>
        )}

        {/* 注册按钮 */}
        <button
          type="submit"
          disabled={isSubmitting || !isFormValid}
          className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-green-300 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              注册中...
            </span>
          ) : (
            '注册'
          )}
        </button>

        {/* 切换到登录 */}
        <div className="text-center">
          <button
            type="button"
            onClick={onSwitchToLogin}
            className="text-blue-600 hover:text-blue-500 text-sm underline"
          >
            已有账号？立即登录
          </button>
        </div>
      </form>
    </div>
  );
} 